import _has from 'lodash/has';

/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

const LANG_EXTENSION = 'x-ibm-languages';
let languageCodes = getLanguageCodes();

// map the browser language ("en_US") to a set of valid language codes that can be
// used in the Swagger. Ie fall back to lang only also. And lower case.
function mapLanguageToCode(language) {
  // take the value and lower case
  let codes = [];
  if (language) {
    codes.push(language.toLowerCase());
    // take lang code alone
    if (language.indexOf('-') !== -1) {
      let langOnly = language.split('-')[0];
      codes.push(langOnly.toLowerCase());
    }
  }
  return codes;
}

// Get an array of language codes to match against in the Open API from the language
// of the browser. This will be the language plus culture code and then the language
// code (if a culture code exists) all in lower case. For example a browser in "en-US"
// this will be ["en-us", "en"]. For a browser in "en" this will be ["en"].
// The code will take the first language only. We could use the whole list, but then
// we could have the situation where translations exist for some languages in some
// places and for some languages in other places, and the page displays a mix of
// however many languages the user said they would accept. It is less complex to
// just take the first accept-language value and fall back to country code then
// swagger.
export function getLanguageCodes() {
  if (typeof window == 'undefined') return;
  let code = null;
  let navigator = window.navigator;
  let options = null;

  if (_has(window, 'apiConnectExplorer.options')) {
    options = window.apiConnectExplorer.options;
  }

  if (_has(window, 'drupalSettings.api.explorer')) {
    options = window.drupalSettings.api.explorer.options;
  } else if (_has(window, 'Drupal.settings.api.explorer')) {
    // support for Drupal7 style Drupal.settings
    options = window.Drupal.settings.api.explorer.options;
  }

  if (options && options.locale) {
    code = options.locale;
  } else if (navigator) {
    // use languages, valid for Chrome and Firefox
    // access as ["languages"] because TypeScript lib.d.ts does not have these
    if (navigator['languages']) {
      // take only first language
      code = navigator['languages'][0];
    } else if (navigator.language) {
      // or just language if not
      code = navigator.language;
    } else if (navigator['browserLanguage']) {
      // or IE specific language
      code = navigator['browserLanguage'];
    }
  }

  code = mapLanguageToCode(code);

  return code;
}

export function getTranslatedExtensions(obj, propertyName) {
  let ret = null;

  if (obj && obj[LANG_EXTENSION] && obj[LANG_EXTENSION][propertyName]) {
    // find translation under LANG_EXTENSION.propertyName.languageCode
    for (let code of languageCodes) {
      if (obj[LANG_EXTENSION][propertyName][code]) {
        ret = obj[LANG_EXTENSION][propertyName][code];
        break;
      }
    }
  }
  if (ret === null && obj && obj[propertyName]) {
    // use swagger
    ret = obj[propertyName];
  }
  return ret;
}

export function replaceDescriptionWithLanguage(obj, language) {
  if (!language) return;
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      replaceDescriptionWithLanguage(obj[key], language);
    }

    if ((key === 'description' || key === 'summary') && obj[LANG_EXTENSION] && obj[LANG_EXTENSION][key]) {
      const translations = obj[LANG_EXTENSION][key];
      if (translations[language]) {
        obj[key] = translations[language];
      }
      delete obj[LANG_EXTENSION];
    }
  });
}
